/** Constants used to fill up our data base. */

import { Injectable } from '@angular/core';
import { labFeaturesEndpoints } from '@lab/features/lab-features.endpoints';
import { GeneratorMap, MpsGenerator } from '@mps/mps-kit/mps-mock/helpers/mps-generator';
import { MpsRepository } from '@mps/mps-kit/mps-mock/repos/mps-repository';
import { MpsMockBaseService } from '@mps/mps-kit/mps-mock/services/mps-mock.service';
import { Ziiko } from '../models/ziiko.interface';

@Injectable({
  providedIn: 'root',
})
export class ZiikosFakerService extends MpsMockBaseService<Ziiko> {
  repository = new MpsRepository<Ziiko>();

  constructor() {
    super();
    this.repository.fill(this.generateMany(100));
  }

  public getBaseRoute(): string {
    return labFeaturesEndpoints.ziikos;
  }

  /**
   * Définition des routes mockées avec leurs handlers
   */
  protected getRoutes(): Record<string, any> {
    return {
      // GET /ziikos/{id} - Récupérer un ziiko par ID
      'GET::/ziikos/{id}': (params: any) => {
        const id = Number(params.routeParams.id);
        return this.repository.findById(id);
      },

      // GET /ziikos - Récupérer tous les ziikos (avec pagination)
      'GET::/ziikos': (params: any) => {
        const page = Number(params.queryParams.page);
        const limit = Number(params.queryParams.limit);
        const allData = this.repository.findAll();
        if (isNaN(page) || isNaN(limit)) {
          return {
            data: allData,
            total: allData.length,
            page: 1,
          };
        }
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = allData.slice(startIndex, endIndex);

        return {
          data: paginatedData,
          total: allData.length,
          page: page,
          limit: limit,
        };
      },

      // POST /ziikos - Créer un nouveau ziiko
      'POST::/ziikos': (params: any, payload: any) => {
        this.repository.create(payload);
        return payload;
      },

      // PUT /ziikos/{id} - Mettre à jour un ziiko
      'PUT::/ziikos/{id}': (params: any, payload: any) => {
        const id = Number(params.routeParams.id);
        return this.repository.update(id, payload);
      },

      // DELETE /ziikos/{id} - Supprimer un ziiko
      'DELETE::/ziikos/{id}': (params: any) => {
        const id = Number(params.routeParams.id);
        this.repository.delete(id);
        return { success: true, message: `Ziiko ${id} deleted` };
      },

      // GET /ziikos/search - Recherche personnalisée
      'GET::/ziikos/search': (params: any) => {
        const query = (params.queryParams.q as string) || '';
        const filtered = this.repository
          .findAll()
          .filter(
            (z) =>
              z.firstname.toLowerCase().includes(query.toLowerCase()) ||
              z.lastname.toLowerCase().includes(query.toLowerCase()) ||
              z.email.toLowerCase().includes(query.toLowerCase())
          );

        return {
          data: filtered,
          total: filtered.length,
          page: 1,
          limit: filtered.length,
        };
      },
    };
  }

  public generateOne(): Ziiko {
    const ziikoMap: GeneratorMap<Ziiko> = {
      id: () => Math.floor(Math.random() * 10000),
      firstname: () => MpsGenerator.firstName(),
      lastname: () => MpsGenerator.lastName(),
      igg: () => MpsGenerator.igg(),
      email: (ctx) => (ctx?.firstname ?? '').toLowerCase() + '.' + (ctx?.lastname ?? '').toLowerCase() + '@example.com',
      role: () => MpsGenerator.pick(['Admin', 'User', 'Manager', 'Guest'])(),
      isActive: () => MpsGenerator.boolean(),
      isExtern: () => MpsGenerator.boolean(),
      lastLogin: () => MpsGenerator.date(),
      createdAt: () => MpsGenerator.date(),

      job: () => MpsGenerator.job(),
      gender: () => MpsGenerator.pick(['M', 'F'])(),
      icon: () => 'person',
      avatar: () => 'https://api.dicebear.com/9.x/pixel-art/svg?seed=' + Math.floor(Math.random() * 100),
    };
    return MpsGenerator.generateFromMap(ziikoMap);
  }
}
